import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useBasket from '../basket/useBasket';
import useCashbackProgram from './useCashbackProgram';
import { COUPON_TYPES } from '../../services/exports/Constants';
import { StoreInterface } from '../../store/types';
import ReduxActions from '../../store/ReduxActions';
import { actionCreators } from '../../store/actions';

/**
 * Keeps a persisted points redemption valid against the current balance, program and basket:
 * clears it when nothing is redeemable, and snaps it to the highest valid option otherwise.
 *
 * Mounted once in OrderFlowProvider rather than in CashbackPointsRow. `cached_order` survives
 * sessions, and the row only renders on the payment screens — so a selection that went stale
 * between visits (points spent, balance dropped below the minimum) was sent unchanged by the
 * mobile cart's /order-validations call, rejected, and the customer never reached the screen
 * that would have corrected it.
 */
export default function useReconcileCashbackPoints(): void {
  const { company, cashback_account } = useSelector((state: StoreInterface) => state.initialData);
  const { coupon_type, cashback_points } = useSelector((state: StoreInterface) => state.order.cached_order);
  const { hasCashbackProgram, getRedemptionOptions } = useCashbackProgram();
  const { actualSubtotal } = useBasket();

  const isCashbackActive = coupon_type === COUPON_TYPES.CASHBACK;

  // Until the menu resolves the basket, the subtotal reads 0 and every option would look invalid.
  const isSubtotalKnown = actualSubtotal > 0;
  // A missing account on a company with a program means initial data is still loading, not a
  // zero balance — the backend creates the account whenever the program is active.
  const isBalanceKnown = !hasCashbackProgram || !!cashback_account;

  const options = useMemo<number[]>(() => getRedemptionOptions(actualSubtotal), [getRedemptionOptions, actualSubtotal]);

  useEffect(() => {
    if (!isCashbackActive || !company || !isSubtotalKnown || !isBalanceKnown) {
      return;
    }

    if (options.length === 0) {
      ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          coupon_type: null,
          coupon_details: null,
          cashback_points: null,
        }),
      );

      return;
    }

    if (cashback_points == null || !options.includes(cashback_points)) {
      ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          cashback_points: options[options.length - 1],
        }),
      );
    }
  }, [isCashbackActive, company, isSubtotalKnown, isBalanceKnown, options, cashback_points]);
}
